/**
 * useVariantDriverSync hook
 *
 * Drives the create-only workflow for authoring a new (pending, unsaved) KPI Variant's drivers from the
 * deckline side panel. The pending variant's `variant_overrides` are the AUTHORITY:
 *
 * - The side-panel dimension pickers read from and write to the pending variant's overrides directly
 *   (page-independent, toggle-driven `{x, x_hierarchy}` pairs — never diffed against this page's
 *   `page_drivers`, which would drop a dimension's hierarchy companion; task P519764861 Defect 1).
 * - When the variant's overrides change, a single effect mirrors them onto every deckline referencing that
 *   pending variant id, so all its decklines display the same drivers. `deckline_drivers` is local/display
 *   only — it is never persisted; the BE derives it from the linked variant at read.
 *
 * Editing EXISTING variants is handled in the KPI Library, not here.
 */

import { KPI_DIMENSION_CONFIG_MAP } from '@constants/page';
import { useDecklinesContext } from '@context/DecklinesContext';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { asKPIDrivers, generateDriverDisplayOptions, readPageDrivers } from '@utils/kpiDriverHelpers';
import { isEqual, isNil } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { EnhancedKPIVariant } from 'types/KPILibraryContext.types';
import { DriverDisplayOptionMap } from 'types/PageContext.types';
import { AnyKPIDrivers, KpiDimension, KpiDriverField, KpiDriverValues } from 'types/PageSidePanel.types';

export interface UseVariantDriverSyncParams {
  /** The selected deckline's linked variant, enhanced with `isNew` (pending) status. */
  selectedEnhancedVariant: EnhancedKPIVariant | null;
  /** The page's per-dataset driver map (inherit source for placeholder display). */
  pageDriverSets: Partial<Record<DatasetSource, AnyKPIDrivers>> | null | undefined;
}

export interface UseVariantDriverSyncResult {
  /** Display options for the new-variant pickers (active = dimension present in the variant's overrides). */
  driverDisplayOptions: DriverDisplayOptionMap;
  /** Page-driver values for the variant's dataset, shown as placeholders on inactive dimensions. */
  placeholderDrivers: AnyKPIDrivers | null;
  /** Apply a dimension value change to the pending variant's overrides. */
  onDriverChange: (key: KpiDimension, updates: KpiDriverValues) => void;
  /** Toggle a dimension on/off in the pending variant's overrides. */
  onDriverToggle: (key: KpiDimension, checked: boolean) => void;
  /** Retarget the pending variant to a different dataset (Source selector), clearing its overrides. */
  changeVariantDataset: (dataset: DatasetSource) => void;
}

/**
 * Hook wiring the new-variant driver pickers to the pending variant (the authority) and propagating its
 * overrides to every linked deckline.
 */
const useVariantDriverSync = ({
  selectedEnhancedVariant,
  pageDriverSets,
}: UseVariantDriverSyncParams): UseVariantDriverSyncResult => {
  const { pageDecklines, updatePageDeckline } = useDecklinesContext();
  const {
    pendingCreations: { pendingVariants, updatePendingVariant },
  } = useKPILibraryContext();

  const isNewVariant = !!selectedEnhancedVariant?.isNew;
  const variantId = selectedEnhancedVariant?.kpi_variant_id;
  const variantDataset = selectedEnhancedVariant?.dataset_source ?? DatasetSource.GALAXY;

  // The pending variant's overrides — the single authority for this workflow.
  const variantOverrides = useMemo<KpiDriverValues>(
    () => pendingVariants.find((v) => v.kpi_variant_id === variantId)?.variantOverrides ?? {},
    [pendingVariants, variantId],
  );

  const placeholderDrivers = useMemo(
    () => readPageDrivers(pageDriverSets, variantDataset),
    [pageDriverSets, variantDataset],
  );

  // Build the picker display options from the variant's overrides. A dimension is active iff it is present
  // in the overrides (page_drivers are intentionally NOT the active source — this is a variant, not a
  // deckline). Mirrors the KPI Library edit view so both driver editors behave identically.
  const driverDisplayOptions = useMemo<DriverDisplayOptionMap>(() => {
    const options = generateDriverDisplayOptions(variantOverrides, undefined, undefined, variantDataset) ?? {};
    return Object.fromEntries(
      Object.entries(options).map(([key, entry]) => {
        const dimensionKey = entry?.kpiDimension as KpiDriverField | undefined;
        const active = !!dimensionKey && !isNil(variantOverrides[dimensionKey]);
        return [key, entry ? { ...entry, active } : entry];
      }),
    );
  }, [variantOverrides, variantDataset]);

  // Propagate the AUTHORITY: mirror the variant's overrides onto every deckline referencing this pending
  // variant id, so all its decklines display the same drivers regardless of page.
  useEffect(() => {
    if (!isNewVariant || !variantId) return;
    const mirroredDrivers = asKPIDrivers({
      ...variantOverrides,
      datasetSource: variantDataset,
      __typename: variantDataset === DatasetSource.HCTR ? 'HctrKPIDrivers' : 'GalaxyKPIDrivers',
    });
    pageDecklines
      .filter((dl) => dl.kpi_variant_id === variantId && !isEqual(dl.deckline_drivers, mirroredDrivers))
      .forEach((dl) => {
        updatePageDeckline(dl.metadata.internalId, 'deckline_drivers', mirroredDrivers);
        // Base-row kpi_id is backend-derived (from the variant) and dropped on save — clear to '' (unset).
        updatePageDeckline(dl.metadata.internalId, 'kpi_id', '');
      });
  }, [isNewVariant, variantId, variantDataset, variantOverrides, pageDecklines, updatePageDeckline]);

  const onDriverChange = useCallback(
    (_key: KpiDimension, updates: KpiDriverValues) => {
      if (!variantId) return;
      updatePendingVariant(variantId, { variantOverrides: { ...variantOverrides, ...updates } });
    },
    [variantId, variantOverrides, updatePendingVariant],
  );

  const onDriverToggle = useCallback(
    (key: KpiDimension, checked: boolean) => {
      if (!variantId) return;
      const { valueAccessor, hierarchyAccessor } = KPI_DIMENSION_CONFIG_MAP[key]?.decklineDrivers ?? {};
      if (!valueAccessor) return;

      if (checked) {
        // Activate the dimension with empty members; the empty value is dropped at the wire boundary until
        // the author picks a value, so a toggled-on-but-unpicked dimension contributes nothing to the save.
        updatePendingVariant(variantId, {
          variantOverrides: {
            ...variantOverrides,
            [valueAccessor]: '',
            ...(hierarchyAccessor && { [hierarchyAccessor]: '' }),
          },
        });
      } else {
        // Deactivate: remove the dimension's pair so it cascades from whatever page the variant is used on.
        const keysToRemove = new Set<string>([valueAccessor, ...(hierarchyAccessor ? [hierarchyAccessor] : [])]);
        const nextOverrides = Object.fromEntries(
          Object.entries(variantOverrides).filter(([k]) => !keysToRemove.has(k)),
        );
        updatePendingVariant(variantId, { variantOverrides: nextOverrides });
      }
    },
    [variantId, variantOverrides, updatePendingVariant],
  );

  const changeVariantDataset = useCallback(
    (dataset: DatasetSource) => {
      if (!variantId || dataset === variantDataset) return;
      // Dimensions differ per dataset, so clear overrides when retargeting. The propagate effect then
      // mirrors the empty set onto the linked decklines.
      updatePendingVariant(variantId, { dataset_source: dataset, variantOverrides: {} });
    },
    [variantId, variantDataset, updatePendingVariant],
  );

  return { driverDisplayOptions, placeholderDrivers, onDriverChange, onDriverToggle, changeVariantDataset };
};

export default useVariantDriverSync;
